import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import MediaQuery from 'react-responsive';
import uniqueId from 'lodash/uniqueId';

import sortAsc from '../../assets/images/sort_asc.svg';
import sortDesc from '../../assets/images/sort_desc.svg';

import './SecuritiesGrid.css';
import Currency from '../Currency';
import Percentage from '../Percentage';

import { LAYOUT, MIN_WIDTH } from '../Layout/enums';

// Determines how cells in a column should be rendered
export const TYPE = Object.freeze({
  LINK: 1,
  NUMBER: 2,
  CURRENCY: 3,
  PERCENTAGE: 4,
});

const SORT_ASC = true;
const SORT_DESC = false;

const DataCell = (cellData, cellIndex, layout, columns) => {
  if (layout <= columns[cellIndex].SmallestLayout) {
    // Desktop = default
    // See comment in ColumnHeadings
    let col0OuterStyle = columns.length === 3 ?
      'columnZeroHeadingOuterPriv' : 'columnZeroHeadingOuter';
    let colOuterStyle = 'columnHeadingOuter';
    let fontSize = 16;
    switch (layout) {
      case LAYOUT.TAB:
        col0OuterStyle = columns.length === 3 ?
          'columnZeroHeadOuterTabPriv' : 'columnZeroHeadOuterTab';
        colOuterStyle = 'columnHeadOuterTab';
        fontSize = 14;
        break;
      case LAYOUT.MOB:
        col0OuterStyle = 'columnZeroHeadOuterMob';
        colOuterStyle = 'columnHeadOuterMob';
        fontSize = 14;
        break;
      default:
        break;
    }

    switch (columns[cellIndex].Type) {
      case TYPE.CURRENCY:
        return (
          <div key={uniqueId()} styleName={colOuterStyle}>
            <Currency style={{ fontSize }} styleName="cellCurrency" amount={cellData} />
          </div>);
      case TYPE.LINK:
        return (
          <div key={uniqueId()} styleName={col0OuterStyle}>
            <Link
              style={{ fontSize }}
              styleName={cellData.disabled ? 'cellLinkDisabled' : 'cellLink'}
              to={cellData.linkTo}
            >{cellData.name}
            </Link>
          </div>);
      case TYPE.NUMBER:
        return (
          <div key={uniqueId()} styleName={colOuterStyle}>
            <div style={{ fontSize }} styleName="cellNumber">{Number(cellData).toFixed(0)}</div>
          </div>
        );
      case TYPE.PERCENTAGE:
        return (
          <div key={uniqueId()} styleName={colOuterStyle}>
            <Percentage style={{ fontSize }} amount={cellData} toFixed={2} />
          </div>
        );
      // no default
    }
  }
  return null;
};

export const DataRowSec = (rowData, layout, columns) => {
  const link = rowData[0].linkTo;
  if (layout < LAYOUT.MOB) {
    // Desktop and Tablet
    return (
      <Link to={link} styleName="dataRow" key={uniqueId()}>
        {rowData.map((cellData, index) => DataCell(cellData, index, layout, columns))}
      </Link>);
  }
  // Mobile layout
  return (
    <Link to={link} styleName="dataRowMob" key={uniqueId()}>
      <div styleName="mobileInnerRow">
        {DataCell(rowData[0], 0, LAYOUT.MOB, columns)}
        {DataCell(rowData[5], 5, LAYOUT.MOB, columns)}
      </div>
      <div styleName="mobileInnerRow">
        <div styleName="mobileSharesContainer">
          {DataCell(rowData[1], 1, LAYOUT.MOB, columns)}
          <div styleName="sharesLabel">Shares</div>
        </div>
        {DataCell(rowData[6], 6, LAYOUT.MOB, columns)}
      </div>
    </Link>);
};

export const DataRowPriv = (rowData, layout, columns) => {
  const link = rowData[0].linkTo;
  if (layout < LAYOUT.MOB) {
    // Desktop and Tablet
    return (
      <Link to={link} styleName="dataRow" key={uniqueId()}>
        {rowData.map((cellData, index) => DataCell(cellData, index, layout, columns))}
      </Link>);
  }
  // Mobile layout
  return (
    <Link to={link} styleName="dataRowMob" key={uniqueId()}>
      <div styleName="mobileInnerRow">
        {DataCell(rowData[0], 0, LAYOUT.MOB, columns)}
        {DataCell(rowData[4], 4, LAYOUT.MOB, columns)}
      </div>
      <div styleName="mobileInnerRow">
        <div styleName="mobileSharesContainer">
          {DataCell(rowData[1], 1, LAYOUT.MOB, columns)}
          <div styleName="sharesLabel">Shares</div>
        </div>
      </div>
    </Link>);
};

class SecuritiesGrid extends Component {
  constructor(props) {
    super(props);

    this.state = {
      data: props.data,
    };
  }

  componentWillReceiveProps(nextProps) {
    this.setState({
      data: nextProps.data,
    });
  }

  sortColumn = (colHeadIndex) => {
    const { columns } = this.props;
    const { data } = this.state;
    const dataToSort = [...data];
    for (let index = 0; index < columns.length; index += 1) {
      const column = columns[index];

      column.Sort = (index === colHeadIndex ? !column.Sort : null);
    }

    switch (columns[colHeadIndex].Type) {
      case TYPE.LINK:
        if (columns[colHeadIndex].Sort) {
          dataToSort.sort((a, b) => (a[colHeadIndex].name >= b[colHeadIndex].name ? 1 : -1));
        } else {
          dataToSort.sort((a, b) => (!(a[colHeadIndex].name >= b[colHeadIndex].name) ? 1 : -1));
        }
        break;
      default:
        if (columns[colHeadIndex].Sort) {
          dataToSort.sort((a, b) => (a[colHeadIndex] - b[colHeadIndex]));
        } else {
          dataToSort.sort((a, b) => (b[colHeadIndex] - a[colHeadIndex]));
        }
        break;
    }

    this.setState({
      ...this.state,
      data: dataToSort,
      sortColumn: colHeadIndex,
    });
  };
  ColumnHeadings = (layout) => {
    const { columns } = this.props;
    // Desktop = default
    let col0Style = 'columnZeroHeading';
    let colStyle = 'columnHeading';
    // Currently the private placements grid is the only
    //  other layout for the SecuritiesGrid component (and it has
    //  three columns).
    //  Will probably need to redo this if we use this component
    //  for another grid.  Make the column widths configurable via
    //  the columns array or maybe even the entire column style.
    let col0OuterStyle = columns.length === 3 ?
      'columnZeroHeadingOuterPriv' : 'columnZeroHeadingOuter';
    let colOuterStyle = 'columnHeadingOuter';
    switch (layout) {
      case LAYOUT.TAB:
        col0Style = 'columnZeroHeadTab';
        colStyle = 'columnHeadTab';
        col0OuterStyle = columns.length === 3 ?
          'columnZeroHeadOuterTabPriv' : 'columnZeroHeadOuterTab';
        colOuterStyle = 'columnHeadOuterTab';
        break;
      case LAYOUT.MOB:
        col0Style = 'columnZeroHeadMob';
        colStyle = 'columnHeadMob';
        break;
      default:
        break;
    }
    if (layout < LAYOUT.MOB) {
      // Desktop and Tablet layouts
      return (
        <div styleName="headingRow">
          {columns.map((column, idx) => {
            if (layout <= column.SmallestLayout) {
              let sortImage = null;

              switch (column.Sort) {
                case SORT_ASC:
                  sortImage = sortAsc;
                  break;
                case SORT_DESC:
                  sortImage = sortDesc;
                  break;
                default:
                  sortImage = null;
                  break;
              }
              return (
                <div key={uniqueId()} styleName={idx === 0 ? col0OuterStyle : colOuterStyle}>
                  <button
                    id={`sortBtn${idx}`}
                    type="button"
                    onClick={() => this.sortColumn(idx)}
                    styleName="columnButton"
                  >
                    <div styleName={idx === 0 ? col0Style : colStyle}>
                      {column.Name}
                      {sortImage ?
                        <img
                          styleName="columnSortImage"
                          src={sortImage}
                          alt={`Sort ${column.Name}`}
                        /> : null}
                    </div>
                  </button>
                </div>
              );
            }
            return null;
          })
          }
        </div>
      );
    }
    // Mobile header layout
    return (
      <div styleName="headingRow">
        <div styleName={col0Style}>
          {columns[0].Name}
        </div>
      </div>
    );
  };
  render() {
    const {
      mediaQueryValues,
      columns,
      RowRender,
    } = this.props;
    const { data } = this.state;
    return (
      <div id="gridArea" styleName="gridArea">
        <MediaQuery
          minWidth={MIN_WIDTH.DESK}
          values={mediaQueryValues}
        >
          {this.ColumnHeadings(LAYOUT.DESK)}
          {data.map(row => RowRender(row, LAYOUT.DESK, columns))}
        </MediaQuery>
        <MediaQuery
          minWidth={MIN_WIDTH.TAB}
          maxWidth={MIN_WIDTH.DESK - 1}
          values={mediaQueryValues}
        >
          {this.ColumnHeadings(LAYOUT.TAB)}
          {data.map(row => RowRender(row, LAYOUT.TAB, columns))}
        </MediaQuery>
        <MediaQuery
          maxWidth={MIN_WIDTH.TAB - 1}
          values={mediaQueryValues}
        >
          {this.ColumnHeadings(LAYOUT.MOB)}
          {data.map(row => RowRender(row, LAYOUT.MOB, columns))}
        </MediaQuery>
      </div>
    );
  }
}

SecuritiesGrid.propTypes = {
  // data objects in the array should have a property
  // for each column in the columns array in the same
  // order
  data: PropTypes.arrayOf(PropTypes.array).isRequired,
  mediaQueryValues: PropTypes.shape({}),
  /*
  Columns
    Name: The heading text to display
    Type: TYPE.LINK, TYPE.NUMBER, TYPE.CURRENCY, TYPE.PERCENTAGE
    Sort: Initial sort direction for column.  Still requires sortColumn
          to be called.
          null, SORT_ASC, SORT_DESC
    SmallestLayout: Restricts column display to certain layouts.  The specified
          layout will be the smallest layout to display the column.
          LAYOUT.DESK, LAYOUT.TAB, LAYOUT.MOB
  */
  columns: PropTypes.arrayOf(PropTypes.shape({
    Name: PropTypes.string,
    Type: PropTypes.number,
    SmallestLayout: PropTypes.number,
  })).isRequired,
  RowRender: PropTypes.func.isRequired,
};

SecuritiesGrid.defaultProps = {
  mediaQueryValues: {},
};

export default SecuritiesGrid;



// WEBPACK FOOTER //
// ./src/components/Portfolio/SecuritiesGrid.js